/**
 * @file Bootstrapper.ts
 * @role THE single composition root. Selects and delegates construction of every service,
 *       wires the clamped clock to the ticker, registers services, and runs the boot sequence: init Pixi ->
 *       ticker -> validate config -> assets -> show splash -> load game bundles (with
 *       retry on failure). Later milestones extend this (RGS session, reel scene)
 *       without adding a second composition root.
 * @owns Dependency selection, service registration, and top-level lifecycle ordering.
 * @reads A host element + the ServiceContainer to fill.
 * @neverDoes Render. Run reel physics. Act as a runtime service locator.
 * @invariants 10 (config validated before use), 13 (one composition root), 21 (boot
 *             always reaches a terminal state: game loaded or a retryable error panel),
 *             22 (GameClock advanced only by GameTicker).
 * @see docs/ARCHITECTURE.md, docs/GAME_FLOW.md
 */
import { BootPipeline } from './BootPipeline';
import { markBootPerformance } from './BootPerformance';
import { hideBootShell } from './BootShell';
import { createCoreServices, type CoreServices } from './CoreServiceFactory';
import { GameSceneFactory } from './GameSceneFactory';
import { SceneDirector } from './SceneDirector';
import { ServiceContainer } from './ServiceContainer';
import { ServiceKeys } from './ServiceKeys';
import { IS_DEV_BUILD, readGameEnvironment } from './GameEnvironment';
import type { EventBus } from '@core/events/EventBus';
import type { AssetService } from '@core/assets/AssetService';
import { textureResolutionForLayout } from '@core/assets/TextureResolutionPolicy';
import { assetManifestIds } from '@core/assets/AssetManifest';
import { waitForMinimumDuration } from '@core/time/MinimumDuration';
import { AudioCueController } from '@core/audio/AudioCueController';
import type { AnalyticsService } from '@core/analytics/AnalyticsService';
import type { DiagnosticsService } from '@core/diagnostics/DiagnosticsService';
import { GameError } from '@core/errors/GameError';
import type { Logger } from '@core/logging/Logger';
import { SplashScene } from '@scenes/SplashScene/SplashScene';
import { ReelConfig } from '@game/reels/ReelConfig';
import { SymbolRegistry } from '@game/reels/SymbolRegistry';
import { PaytableConfig, bundledPaytableConfig } from '@core/config/PaytableConfig';
import { SkinRegistry } from './GameSkin';
import { WinConfig } from '@game/wins/WinConfig';
import { createRgsClient } from '@core/backend/RgsClientFactory';
import { EconomyPresenter } from '@game/economy/EconomyPresenter';
import { SystemWallClock } from '@core/time/WallClock';
import type { AssetBundleName } from '@core/assets/AssetBundleName';
import { FullscreenConsent } from './FullscreenConsent';
import { BrowserFullscreenService } from '@core/viewport/FullscreenService';
import { isCoarsePointer } from '@core/layout/PointerCapability';
import { alternateBackgroundBundles, backgroundForLayout } from './ResponsiveBackgrounds';

/** Minimum bundles required to enter gameplay, loaded concurrently behind the splash. */
const CRITICAL_GAME_BUNDLES: readonly AssetBundleName[] = ['core', 'ui', 'reels'];
const PREVIEW_BUNDLE: AssetBundleName = 'preview';
const FREE_SPINS_BUNDLE: AssetBundleName = 'free_spins';
const OPTIONAL_MOTION_BUNDLE: AssetBundleName = 'reel_motion';

export class Bootstrapper {
  private audioCues: AudioCueController | undefined;
  private audioUnlocked = false;
  private fullscreen: BrowserFullscreenService | undefined;
  private gameInteractive = false;

  constructor(
    private readonly host: HTMLElement,
    private readonly container = new ServiceContainer(),
  ) {}

  async run(): Promise<ServiceContainer> {
    markBootPerformance('boot:start');
    const environment = readGameEnvironment();
    const symbolRegistry = new SymbolRegistry();
    // Validated here, at the composition root: a payout keyed by a symbol the server has never
    // heard of fails the boot rather than reaching a player's screen.
    const paytable = new PaytableConfig(bundledPaytableConfig, symbolRegistry.ids());
    const skinRegistry = new SkinRegistry(
      symbolRegistry.ids(),
      assetManifestIds(),
      environment.gameSkinId,
    );
    const services = createCoreServices(this.host, skinRegistry.active, environment);
    const {
      logger,
      eventBus,
      app,
      ticker,
      clock,
      layout,
      assets,
      config,
      uiConfig,
      settings,
      graphicsQuality,
      analytics,
      audio,
      particles,
      postFx,
      ambientFx,
      resolveTexture,
      diagnostics,
      skin,
    } = services;

    const mockPreviewBadge = document.getElementById('mock-preview-badge');
    if (mockPreviewBadge) mockPreviewBadge.hidden = !environment.isMockPreview;
    if (skinRegistry.usedFallback) {
      logger.warn(
        `[skin] unknown VITE_GAME_SKIN "${skinRegistry.requestedId}"; using "${skin.id}"`,
      );
    }

    // Local settings are validated before renderer creation because antialiasing is a
    // context-creation choice. Unknown device capability resolves Auto to Balanced.
    settings.load();
    graphicsQuality.select(settings.get().graphicsQuality);
    const initialGraphics = graphicsQuality.profile;

    // 2. Boot the renderer + clock so the stage and SceneDirector exist.
    await app.init(this.host, initialGraphics.antialias);
    // Dev-only devtools bridge. The static IS_DEV_BUILD guard lets Vite drop the dynamic
    // import from production bundles; installDevtools swallows and logs its own failures.
    if (IS_DEV_BUILD) {
      const { installDevtools } = await import('./installDevtools');
      await installDevtools(app, logger);
    }
    app.connectLayout(
      layout,
      Math.min(uiConfig.layoutPolicy.maxDevicePixelRatio, initialGraphics.maxDevicePixelRatio),
    );
    ticker.attach(app.ticker);
    // Single clock: game time advances only on clamped ticker dt (Invariant 22).
    ticker.add((dt) => {
      clock.advance(dt);
      graphicsQuality.observeFrame(dt);
    });
    this.wireRuntimeListeners(services);
    layout.start();

    const sceneDirector = new SceneDirector(app.stage, layout, ticker);
    sceneDirector.start();
    this.registerServices(services, sceneDirector);

    // Cross-cutting presentation observers subscribe to notifications only; none controls flow.
    this.audioCues?.dispose();
    this.audioCues = new AudioCueController(eventBus, audio);
    this.wireAnalytics(eventBus, analytics);
    this.wireDiagnostics(eventBus, diagnostics);

    // 4. Validate config (trust boundary, Invariant 10) and init assets.
    try {
      config.load();
    } catch (error) {
      diagnostics.record('config', error instanceof GameError ? error.code : 'CONFIG_LOAD_FAILED');
      throw error;
    }
    // Drive audio + the selected graphics profile from one validated settings snapshot.
    settings.subscribe((s) => {
      audio.applySettings(s);
      graphicsQuality.select(s.graphicsQuality);
      this.preloadMusicWhenEligible(services);
    });
    graphicsQuality.subscribe((profile) => {
      app.setMaxDevicePixelRatio(
        Math.min(uiConfig.layoutPolicy.maxDevicePixelRatio, profile.maxDevicePixelRatio),
      );
      app.setMaxFps(profile.maxFps);
      postFx.setQuality(profile.effectsQuality);
      particles.setDensityMultiplier(profile.particleMultiplier);
      ambientFx.setDensityMultiplier(profile.ambientMultiplier);
    });
    const reelConfig = new ReelConfig();
    const winConfig = new WinConfig();
    const reelPresentationConfig = skin.reels;

    // Create the RGS client and START the session round-trip now, so the network wait
    // overlaps asset loading behind the splash instead of following it (mock is dev/test
    // only; production fails loud). Client construction stays OUTSIDE any retry loop: its
    // failure mode (mock enabled in production) is a configuration error a user retry can
    // never fix. A failed pre-started attempt is surfaced by the session retry loop below;
    // user retries there issue a fresh createSession().
    const rgsClient = createRgsClient({
      environment,
      gameConfig: config.game,
    });
    const bootPipeline = new BootPipeline({
      assets,
      app,
      resolveTexture,
      rgsClient,
      reelShape: { columns: reelConfig.columns, rows: reelConfig.rows },
      symbolIds: [...symbolRegistry.ids()],
      logger,
      diagnostics,
    });
    bootPipeline.prestartSession();

    await assets.init(textureResolutionForLayout(layout.context, uiConfig.layoutPolicy));
    const initialBackground = backgroundForLayout(layout.context, uiConfig);
    await Promise.all([assets.loadBundle('splash'), assets.loadBundle(initialBackground.bundle)]);

    // 5. Show splash, THEN load game bundles so the splash displays their progress.
    const startupBundles = environment.skinPreviewRequested
      ? [...CRITICAL_GAME_BUNDLES, PREVIEW_BUNDLE]
      : CRITICAL_GAME_BUNDLES;
    // Keep the live lockup out of the static entry without duplicating its implementation. The
    // HTML shell remains visible while this small presentation chunk arrives, and the later game
    // scene reuses the same cached module.
    const { GameTitleView } = await import('@ui/hud/GameTitleView');
    const splash = new SplashScene(
      eventBus,
      uiConfig,
      new GameTitleView(uiConfig.splash, uiConfig.gameTitle),
      startupBundles,
      resolveTexture,
      initialBackground.assetId,
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    );
    await sceneDirector.show(splash);
    markBootPerformance('splash:visible');
    // Best-effort and intentionally outside the critical bundle/retry gate. Only discovered
    // source pairs create handles or requests, and a slow cue never extends the splash.
    void audio.preloadGroup('startup');
    const gameSceneFactory = new GameSceneFactory();
    const gameScenePreload = gameSceneFactory.preload();
    void gameScenePreload.catch(() => undefined);
    // The splash is live — retire the static HTML loader only now, so a slow splash-bundle
    // fetch never exposes a blank canvas between the two loading surfaces.
    hideBootShell();
    const splashShownAtMs = performance.now();

    const bootResult = await bootPipeline.run({
      retrySurface: splash,
      startupBundles,
    });
    const { session } = bootResult;
    analytics.track('session_created', { gameId: session.gameId, currency: session.currency });
    const economy = new EconomyPresenter(
      session.currency,
      session.balanceMinor,
      session.activeCampaignRoundsRemaining !== undefined &&
        session.activeCampaignRoundsRemaining !== null
        ? (session.betAmountMinor ?? config.game.defaultBetMinor)
        : config.game.defaultBetMinor,
    );
    // Begin optional feature art only after the critical gate. A feature award awaits this same
    // promise before its intro, so slow networks cannot reveal an empty backside or delay boot.
    const freeSpinsAssetsReady = this.loadOptionalBundle(
      assets,
      FREE_SPINS_BUNDLE,
      logger,
      diagnostics,
    );
    // 7. Bundles + session ready — enter the main game scene.
    await waitForMinimumDuration(splashShownAtMs, uiConfig.splash.minimumVisibleMs);
    await gameScenePreload;
    // Ask touch-device players once whether to play fullscreen. Their tap is the only moment
    // a browser will grant it. Skips silently on desktop, where fullscreen is unsupported,
    // when already fullscreen, or once the player has answered before. Resolves on every path
    // so boot can never hang here.
    // One instance for the whole session: the consent modal asks with it, and the settings
    // panel's fullscreen row drives and observes it afterwards.
    this.fullscreen?.dispose();
    const fullscreen = new BrowserFullscreenService();
    this.fullscreen = fullscreen;
    await new FullscreenConsent({
      fullscreen,
      coarsePointer: isCoarsePointer(),
      ignoreStoredAnswer: environment.fullscreenConsentReset,
    }).ask();
    await sceneDirector.show(
      await gameSceneFactory.create({
        bootResult,
        session: {
          rgsClient,
          economy,
          eventBus,
        },
        presentation: {
          logger,
          diagnostics,
          resolveTexture,
          prepareFreeSpinsAssets: () => freeSpinsAssetsReady,
          settingsService: settings,
          graphicsQuality,
          analytics,
          particles,
          postFx,
          ambientFx,
          audio,
          wallClock: new SystemWallClock(),
          accessibilityHost: this.host,
          reducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
          fullscreen,
          environment,
        },
        configuration: {
          title: config.game.gameId,
          reelConfig,
          reelPresentationConfig,
          paytable,
          scatterSymbolId: config.game.rgs.scatterSymbolId,
          symbolRegistry,
          winConfig,
          uiConfig,
          symbolFx: skin.symbolFx,
          presentationSequences: skin.presentationSequences,
          betStepsMinor: config.game.betStepsMinor,
          spinTimeoutMs: config.game.spinTimeoutMs,
        },
      }),
    );
    markBootPerformance('game:interactive');
    this.gameInteractive = true;
    // The initial orientation alone is boot-critical. Cache alternatives after interaction so a
    // later layout-mode switch can repaint immediately without charging both decoded textures to
    // the startup budget.
    void Promise.all(
      alternateBackgroundBundles(uiConfig, initialBackground).map((bundle) =>
        this.loadOptionalBundle(assets, bundle, logger, diagnostics),
      ),
    ).then(() => sceneDirector.refreshLayout());
    this.preloadMusicWhenEligible(services);

    eventBus.emit('game:loaded');
    let motionLoadStarted = false;
    graphicsQuality.subscribe((profile) => {
      if (!profile.motionTextures || motionLoadStarted) return;
      motionLoadStarted = true;
      void this.loadOptionalBundle(assets, OPTIONAL_MOTION_BUNDLE, logger, diagnostics);
    });
    return this.container;
  }

  private registerServices(services: CoreServices, sceneDirector: SceneDirector): void {
    this.container.register(ServiceKeys.GameEnvironment, services.environment);
    this.container.register(ServiceKeys.Logger, services.logger);
    this.container.register(ServiceKeys.EventBus, services.eventBus);
    this.container.register(ServiceKeys.GameApplication, services.app);
    this.container.register(ServiceKeys.GameTicker, services.ticker);
    this.container.register(ServiceKeys.GameClock, services.clock);
    this.container.register(ServiceKeys.LayoutService, services.layout);
    this.container.register(ServiceKeys.AssetService, services.assets);
    this.container.register(ServiceKeys.ConfigService, services.config);
    this.container.register(ServiceKeys.UiConfig, services.uiConfig);
    this.container.register(ServiceKeys.GameSkin, services.skin);
    this.container.register(ServiceKeys.SettingsService, services.settings);
    this.container.register(ServiceKeys.GraphicsQualityService, services.graphicsQuality);
    this.container.register(ServiceKeys.AudioService, services.audio);
    this.container.register(ServiceKeys.AnalyticsService, services.analytics);
    this.container.register(ServiceKeys.ParticleService, services.particles);
    this.container.register(ServiceKeys.PostFxService, services.postFx);
    this.container.register(ServiceKeys.AmbientFxService, services.ambientFx);
    this.container.register(ServiceKeys.DiagnosticsService, services.diagnostics);
    this.container.register(ServiceKeys.SceneDirector, sceneDirector);
  }

  private wireRuntimeListeners(services: CoreServices): void {
    const { ticker, audio } = services;
    // Tab-focus recovery: pause physics while hidden so reels never advance on a throttled
    // rAF clock and teleport on return (Invariant 22). Network is unaffected (not ticked).
    if (typeof document === 'undefined') return;
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) ticker.pause();
      else ticker.resume();
    });

    // Resume the audio context on the first user gesture (browsers gate autoplay).
    const unlock = (): void => {
      audio.unlock();
      this.audioUnlocked = true;
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('keydown', unlock);
      this.preloadMusicWhenEligible(services);
    };
    document.addEventListener('pointerdown', unlock);
    document.addEventListener('keydown', unlock);
  }

  private preloadMusicWhenEligible(services: Pick<CoreServices, 'audio' | 'settings'>): void {
    if (!this.audioUnlocked || !this.gameInteractive) return;
    if (services.settings.get().musicVolume <= 0) return;
    void services.audio.preloadGroup('music');
  }

  /** Subscribe analytics to notifications (fire-and-forget; never blocks gameplay). */
  private wireAnalytics(eventBus: EventBus, analytics: AnalyticsService): void {
    eventBus.on('game:loaded', () => analytics.track('game_loaded'));
    eventBus.on('spin:started', () => analytics.track('spin_started'));
    eventBus.on('spin:completed', ({ totalWinMinor, hadFeature }) => {
      analytics.track('spin_completed', { totalWinMinor });
      if (totalWinMinor > 0) analytics.track('win_presented', { totalWinMinor });
      if (hadFeature) analytics.track('bonus_triggered');
    });
    eventBus.on('error:shown', ({ code }) => analytics.track('error_shown', { code }));
    eventBus.on('orientation:changed', (orientation) =>
      analytics.track('orientation_changed', { orientation }),
    );
  }

  /** Observe terminal failures using only categories and codes; no player data enters diagnostics. */
  private wireDiagnostics(eventBus: EventBus, diagnostics: DiagnosticsService): void {
    eventBus.on('error:shown', ({ code }) => diagnostics.record('rgs', code));
  }

  /** Load presentation-only art without delaying gameplay or surfacing a blocking retry panel. */
  private loadOptionalBundle(
    assets: AssetService,
    bundleName: AssetBundleName,
    logger: Logger,
    diagnostics: DiagnosticsService,
  ): Promise<void> {
    return assets.loadBundle(bundleName).catch((error: unknown) => {
      const code = error instanceof GameError ? error.code : 'ASSET_BUNDLE_FAILED';
      logger.warn(
        `[assets] optional bundle "${bundleName}" failed; current textures remain active`,
      );
      diagnostics.record('assets', code);
    });
  }
}
